// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, useCurrentUser, AppFooter, apiErrorMessage } from "./common";

const STATUS_LABEL = { planned: "예정", in_progress: "진행중", completed: "완료", cancelled: "취소" };
const STATUS_COLOR = { planned: "gray", in_progress: "orange", completed: "green", cancelled: "rose" };
const RISK_LABEL = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR = { critical: "rose", warning: "amber", stable: "green" };

function fmtTimeRange(iso, durMin) {
  const start = new Date(iso);
  const end = new Date(start.getTime() + (durMin||60)*60000);
  const fmt = d => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  return `${fmt(start)} → ${fmt(end)}`;
}

// Day index for the Mon..Sun week layout. JS getDay() returns 0..6 with
// Sunday = 0, so we shift to make Monday = 0.
function todayWeekIndex() {
  return (new Date().getDay() + 6) % 7;
}

const POLL_INTERVAL_MS = 60_000;

function DashboardCareManager() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const { user } = useCurrentUser();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/v1/dashboard/caremanager", { credentials: "same-origin" });
        const body = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok) {
          // 401: 로그인 안내, 403: 권한 안내, 그 외: 백엔드 메시지/상태코드.
          // 본문을 그대로 data로 흘리지 않도록 분기해 잘못된 렌더 방지.
          if (r.status === 401)      setErr("로그인이 필요합니다. /login 으로 이동해 다시 시도하세요.");
          else if (r.status === 403) setErr(body?.message || "케어매니저 대시보드 접근 권한이 없습니다.");
          else                       setErr(apiErrorMessage(body, r.status));
          return;
        }
        setErr(null);
        setData(body);
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  // 60s silent polling — picks up new match proposals + status changes
  // pushed in from the field (mobile manager check-ins). Slower cadence
  // than the region dashboard because the data here is single-user-scoped
  // and the field updates are inherently slower.
  React.useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") setRefreshTick(t => t + 1);
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  if (err)   return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>API 오류: {err}</div>;
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const m = data.manager;
  const k = data.kpis;
  const next = data.next_visit;

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="manager" active="dashboard"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title={`${user?.name || m.name} 매니저의 오늘`} sub={`${new Date().toLocaleDateString('ko-KR')} · ${m.organization_name||''} · ${m.qualification||''}`}/>
          <div className="main">

            <div className="card" style={{padding:'22px 26px', background:'linear-gradient(120deg, var(--orange-50) 0%, var(--bg-sub) 100%)', border:'1px solid var(--orange-100)', marginBottom:18, display:'grid', gridTemplateColumns:'1.5fr 1fr 1fr 1fr', gap:24, alignItems:'center'}}>
              <div>
                <div style={{fontSize:11, fontWeight:700, color:'var(--orange-700)', marginBottom:6}}>오늘 일정</div>
                <div className="h1" style={{fontSize:24}}>방문 <span style={{color:'var(--orange-600)'}}>{k.today_visits}건</span></div>
                {next && (
                  <div style={{marginTop:8, display:'flex', alignItems:'flex-start', gap:8, flexWrap:'wrap'}}>
                    {/* 본문 — 클릭 시 데스크톱 방문 상세로 진입. 안쪽에 모든 메타 정보. */}
                    <a href={`/visit?id=${next.id}`}
                       title={`${next.elderly_name} 어르신 방문 상세로`}
                       style={{display:'block', flex:'1 1 auto', minWidth:0,
                               padding:'4px 6px', margin:'-4px -6px',
                               textDecoration:'none', color:'inherit', borderRadius:6}}>
                      <div className="muted" style={{fontSize:12, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                        <span>다음 —</span>
                        <b style={{color:'var(--ink-1)'}}>{new Date(next.scheduled_at).toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})} {next.elderly_name} 어르신</b>
                        {next.elderly_age != null && <span>· {next.elderly_age}세</span>}
                        {next.risk_level && <span className={"badge badge-" + RISK_COLOR[next.risk_level]} style={{fontSize:10}}>{RISK_LABEL[next.risk_level]}</span>}
                        <span style={{color:'var(--orange-600)', fontSize:11, fontWeight:700}}>상세 →</span>
                      </div>
                      {next.address && <div className="muted" style={{fontSize:11, marginTop:3}}>{next.address}</div>}
                    </a>
                    {/* 보조 액션 — 지도로 어르신 위치 미리 보기. /visit 와 동급으로 자주 쓰는 동작이라 inline 노출. */}
                    <a href={`/map?visit_id=${next.id}`}
                       title="지도에서 이 어르신 위치 보기"
                       className="btn btn-ghost btn-sm"
                       style={{textDecoration:'none', whiteSpace:'nowrap', flex:'0 0 auto', alignSelf:'center'}}>
                      {Ic.map} 지도
                    </a>
                  </div>
                )}
              </div>
              {[
                {l:'완료 방문', v:`${k.today_done}/${k.today_visits}`, s:'', href:'/schedule'},
                {l:'이번 주 시간', v:`${k.weekly_hours}h`, s:`주 ${k.max_hours}h 기준 ${Math.round(k.weekly_hours/k.max_hours*100)}%`, href:'/schedule'},
                {l:'담당 어르신', v:`${k.assigned}명`, s:`위험 ${k.critical}건`, href:'/cases'},
              ].map((s,i)=>(
                <a key={i} href={s.href} style={{borderLeft:'1px solid var(--border)', paddingLeft:18, textDecoration:'none', color:'inherit', cursor:'pointer'}}>
                  <div className="muted" style={{fontSize:11, fontWeight:600}}>{s.l}</div>
                  <div style={{fontSize:24, fontWeight:800, color:'var(--ink-1)'}}>{s.v}</div>
                  <div className="muted" style={{fontSize:11}}>{s.s}</div>
                </a>
              ))}
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:14}}>
              <div className="card">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 14px'}}>
                  <div className="h3">오늘의 방문 (시간순)</div>
                  <a href="/map" className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>{Ic.map} 동선 보기</a>
                </div>
                {data.today_visits.length === 0 && <div style={{padding:'30px 22px'}} className="muted">오늘 예정된 방문이 없습니다.</div>}
                {data.today_visits.map((v, i)=>{
                  const isNext = next && v.id === next.id;
                  return (
                    <a key={v.id} href={`/visit?id=${v.id}`} style={{display:'flex', gap:14, padding:'14px 22px', borderTop: i>0?'1px solid var(--border-soft)':'none', background: isNext?'linear-gradient(90deg, var(--orange-50), transparent)':'transparent', textDecoration:'none', color:'inherit', cursor:'pointer'}}>
                      <div style={{minWidth:64, fontSize:12, fontWeight:700, color:isNext?'var(--orange-700)':'var(--ink-2)'}}>{v.time}</div>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:13.5, fontWeight:600}}>{v.elderly_name} 어르신</div>
                        <div className="muted" style={{fontSize:11, marginTop:2}}>{v.address}</div>
                      </div>
                      <span className={"badge badge-" + STATUS_COLOR[v.status]}>{STATUS_LABEL[v.status]}</span>
                    </a>
                  );
                })}
              </div>

              <div style={{display:'flex', flexDirection:'column', gap:14}}>
                <div className="card" style={{padding:20, background:'linear-gradient(160deg, #FCEEE3, #FBF7EE)'}}>
                  <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:10}}>
                    <span style={{width:26, height:26, borderRadius:8, background:'var(--orange-500)', color:'#fff', display:'grid', placeItems:'center', fontWeight:800, fontSize:10}}>AI</span>
                    <div className="h3" style={{fontSize:14}}>새 매칭 제안 ({data.match_proposals_count}건)</div>
                  </div>
                  {data.match_proposals_count > 0 ? (
                    <div className="muted" style={{fontSize:12}}>대기 중인 매칭 제안이 있습니다. <a href="/cod" className="btn btn-soft btn-sm" style={{marginLeft:6, textDecoration:'none'}}>확인 →</a></div>
                  ) : (
                    <div className="muted" style={{fontSize:12}}>대기 중인 제안 없음</div>
                  )}
                </div>

                <div className="card card-pad">
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
                    <div className="h3">이번 주 활동량</div>
                    <span className="badge badge-green">{k.weekly_hours}h / {k.max_hours}h</span>
                  </div>
                  {(() => {
                    // 막대 스케일은 8h(평일 풀데이 기준)을 바닥값으로 두되,
                    // 한 칸이라도 그보다 길면 가장 긴 날에 맞춘다 — 8h 초과 시
                    // 막대가 카드를 넘어가는 시각적 오버플로 방지.
                    const dailyHours = ['월','화','수','목','금','토','일']
                      .map((_, i) => data.weekly_hours_by_day?.[i] ?? 0);
                    const scaleMax = Math.max(8, ...dailyHours);
                    return (
                      <div style={{display:'flex', gap:12, alignItems:'flex-end', marginTop:6}}>
                        {['월','화','수','목','금','토','일'].map((d,i)=>{
                          const h = dailyHours[i];
                          const isToday = i === todayWeekIndex();
                          return (
                            <div key={i} style={{flex:1, textAlign:'center'}}>
                              <div style={{height:80, background:'var(--bg-sub)', borderRadius:6, position:'relative', overflow:'hidden'}}>
                                <div style={{position:'absolute', bottom:0, left:0, right:0, height:`${(h/scaleMax)*100}%`, background: isToday?'var(--orange-500)':'var(--green-500)', borderRadius:'4px 4px 0 0'}}/>
                              </div>
                              <div style={{fontSize:11, color: isToday?'var(--orange-700)':'var(--ink-3)', marginTop:6, fontWeight: isToday?800:600}}>{d}</div>
                              <div className="mono" style={{fontSize:10, color:'var(--ink-2)'}}>{h}h</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}
export { DashboardCareManager };
